// ── Chord definitions ────────────────────────────────────────────────────
export const CHORD_PREFIX_HINTS: Record<string, string> = {
  'a': 'a ... s=Service  d=Database  a=API  p=Person  q=Queue  e=External  y=System  x=Decision  i=Diamond  g=Group  b=Box  n=Note  t=Text',
  'l': 'l ... n=New Layer  d=Dim  h=Hide/Show',
  'n': 'n ... i=New input socket  o=New output socket',
  'r': 'r ... n=Rotate node direction (LTR/TTB/RTL)',
  'c': 'c ... c=Color (then 0-9 preset, or c again for picker)  +=Curve Up  -=Curve Down',
  't': 't ... p=Palette  i=Info  g=Pages  a=Animate selected  e=Every  f=Focus (distraction-free)',
  't+e': 't+e ... a=Animate all edges',
  'd': 'd ... b=Box t=Text n=Note o=Oval r=Rounded i=Diamond c=Cylinder h=Hex v=Triangle a=Arrow l=Line f=Frame · dd=sticky',
  'd+d': 'sticky d ... same shape keys',
  'c+c': 'c+c ... 0-9 preset  c=picker',
};
export const CHORD_PREFIXES = new Set(Object.keys(CHORD_PREFIX_HINTS));

// Maps chord string → node type string
export const NODE_CHORD_MAP: Record<string, string> = {
  'a+s': 'service', 'a+d': 'database', 'a+a': 'api',
  'a+p': 'person',  'a+q': 'queue',    'a+e': 'external',
  'a+y': 'system',  'a+x': 'decision', 'a+i': 'diamond',
  'a+g': 'group',
  'a+b': 'box',
  'a+n': 'note',
};

// ── Smart placement ──────────────────────────────────────────────────────
const H_GAP = 60;   // horizontal gap between node edges
const V_GAP = 40;   // vertical gap between node edges
const PADDING = 16; // extra clearance for overlap test

export function findSmartPosition(
  anchorNode: { position: { x: number; y: number }; size: { width: number; height: number } },
  newSize: { width: number; height: number },
  allNodes: Array<{ position: { x: number; y: number }; size: { width: number; height: number } }>,
  allGroups: Array<{ position: { x: number; y: number }; size: { width: number; height: number } }>,
): { x: number; y: number } {
  const { x: ax, y: ay } = anchorNode.position;
  const { width: aw, height: ah } = anchorNode.size;
  const { width: nw, height: nh } = newSize;

  // Candidate positions in preference order:
  //   right → below → right-below → right-above → left → above
  const candidates = [
    { x: ax + aw + H_GAP,  y: ay },                         // right, aligned top
    { x: ax,               y: ay + ah + V_GAP },            // below, aligned left
    { x: ax + aw + H_GAP,  y: ay + ah / 2 - nh / 2 },      // right, vertically centred
    { x: ax + aw + H_GAP,  y: ay + ah + V_GAP },            // right-below
    { x: ax + aw + H_GAP,  y: ay - nh - V_GAP },            // right-above
    { x: ax - nw - H_GAP,  y: ay },                         // left
    { x: ax,               y: ay - nh - V_GAP },            // above
  ];

  const obstacles = [
    ...allNodes.map(n => ({ ...n.position, w: n.size.width, h: n.size.height })),
    ...allGroups.map(g => ({ ...g.position, w: g.size.width, h: g.size.height })),
  ];

  const free = (cx: number, cy: number) =>
    !obstacles.some(o =>
      cx < o.x + o.w + PADDING && cx + nw + PADDING > o.x &&
      cy < o.y + o.h + PADDING && cy + nh + PADDING > o.y
    );

  for (const c of candidates) {
    if (free(c.x, c.y)) return c;
  }

  // Fallback: push far right of rightmost obstacle on the same row
  const maxRight = obstacles.reduce((m, o) => Math.max(m, o.x + o.w), ax + aw);
  return { x: maxRight + H_GAP, y: ay };
}
